import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  AppointmentSlot,
  BookingSession,
  ContactPreferences,
  LabLocation,
  Patient,
  ServiceType,
} from '../models';
import { BookingSessionService } from '../services';

@Injectable({
  providedIn: 'root',
})
export class BookingSessionStore {
  private readonly initialState: BookingSession = {
    selectedPatient: null,
    selectedService: null,
    selectedLocation: null,
    selectedSlot: null,
    confirmationCode: null,
    holdExpiresAt: null,
    mobilePhone: null,
    phoneValidated: false,
    contactPreferences: null,
    returnToReview: false,
    isFromFavoriteLocation: false,
    favoriteLocationId: null,
    createdAt: Date.now(),
    expiresAt: Date.now() + 30 * 60 * 1000,
    lastUpdated: Date.now(),
  };

  private readonly state$ = new BehaviorSubject<BookingSession>(
    this.initialState
  );

  constructor(private sessionService: BookingSessionService) {
    // Load from session storage on init
    const saved = this.sessionService.load();
    if (saved) {
      this.state$.next(saved);
    }
  }

  // Selectors
  getState(): Observable<BookingSession> {
    return this.state$.asObservable();
  }

  getCurrentState(): BookingSession {
    return this.state$.value;
  }

  getSelectedPatient(): Observable<Patient | null> {
    return new BehaviorSubject(
      this.state$.value.selectedPatient
    ).asObservable();
  }

  getSelectedService(): Observable<ServiceType | null> {
    return new BehaviorSubject(
      this.state$.value.selectedService
    ).asObservable();
  }

  getSelectedLocation(): Observable<LabLocation | null> {
    return new BehaviorSubject(
      this.state$.value.selectedLocation
    ).asObservable();
  }

  getSelectedSlot(): Observable<AppointmentSlot | null> {
    return new BehaviorSubject(this.state$.value.selectedSlot).asObservable();
  }

  // Actions
  setSelectedPatient(patient: Patient | null): void {
    this.updateState({ selectedPatient: patient });
  }

  setSelectedService(service: ServiceType | null): void {
    this.updateState({ selectedService: service });
  }

  setSelectedLocation(location: LabLocation | null): void {
    this.updateState({ selectedLocation: location });
  }

  setSelectedSlot(slot: AppointmentSlot | null): void {
    this.updateState({ selectedSlot: slot });
  }

  setMobilePhone(phone: string | null, validated: boolean = false): void {
    this.updateState({ mobilePhone: phone, phoneValidated: validated });
  }

  setContactPreferences(preferences: ContactPreferences | null): void {
    this.updateState({ contactPreferences: preferences });
  }

  setHoldAppointment(confirmationCode: string, holdExpiresAt: number): void {
    this.updateState({ confirmationCode, holdExpiresAt });
  }

  clearHoldAppointment(): void {
    this.updateState({ confirmationCode: null, holdExpiresAt: null });
  }

  setReturnToReview(returnToReview: boolean): void {
    this.updateState({ returnToReview });
  }

  setFavoriteLocationMode(locationId: number): void {
    this.updateState({
      isFromFavoriteLocation: true,
      favoriteLocationId: locationId,
    });
  }

  clearFavoriteLocationMode(): void {
    this.updateState({
      isFromFavoriteLocation: false,
      favoriteLocationId: null,
    });
  }

  clearSession(): void {
    this.state$.next(this.initialState);
    this.sessionService.clear();
  }

  private updateState(updates: Partial<BookingSession>): void {
    const current = this.state$.value;
    const updated = {
      ...current,
      ...updates,
      lastUpdated: Date.now(),
    };
    this.state$.next(updated);
    this.sessionService.save(updated);
  }
}
